import { useEffect, useSyncExternalStore } from 'react';
import { combinedCapture, type CaptureSource } from '@cerebro-diem/core';
import { insertCapture } from './captures';
import { getSession } from './supabase';
import { useAuthStore } from '../stores/authStore';
import { addCapture, loadCaptures, removeCapture, updateCapture, CaptureStorageError } from './captureStorage';
import type { CaptureRecording, PendingCapture } from './captureStorage';
import { transcribeRecording } from './transcription';
import { transferRawRecordingToCapture } from './rawRecordings';

const BACKOFF_MS = [5_000, 15_000, 60_000, 300_000];
type QueueState = { captures: PendingCapture[]; error: string | null; loading: boolean };
let current: QueueState = { captures: [], error: null, loading: true };
let initializing: Promise<void> | undefined;
const listeners = new Set<() => void>();
const sending = new Set<string>();
const snapshot = () => current;
function publish(next: QueueState) { current = next; listeners.forEach(listener => listener()); }
function problem(error: unknown) {
  const message = error instanceof CaptureStorageError ? error.message : 'Browser storage could not complete the operation. Your input remains recoverable.';
  publish({ ...current, error: message, loading: false });
  return message;
}
async function refresh(): Promise<void> {
  try { publish({ captures: await loadCaptures(), error: null, loading: false }); }
  catch (error) { problem(error); throw error; }
}
async function initialize(): Promise<void> {
  if (current.error) initializing = undefined;
  if (!initializing) initializing = refresh().catch(error => { initializing = undefined; throw error; });
  await initializing;
  if (current.error) throw new CaptureStorageError('read_failed');
}
function subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function resetPendingCaptures() { current = { captures: [], error: null, loading: true }; initializing = undefined; sending.clear(); }
export function useCaptureQueue() {
  const state = useSyncExternalStore(subscribe, snapshot);
  useEffect(() => { void initialize().catch(error => problem(error)); }, []);
  return state;
}
/** Never reveal another signed-in account's locally waiting words or recordings. */
export function usePendingCaptures(): PendingCapture[] {
  const userId = useAuthStore(state => state.user?.id);
  return useCaptureQueue().captures.filter(capture => capture.userId === userId);
}

/** Resolves only after the browser's transaction completes. No page-memory fallback. */
export async function addPendingCapture(userId: string, text: string, source: CaptureSource, recording?: CaptureRecording, identity?: { id: string; createdAt: string }, rawRecordingId?: string): Promise<PendingCapture> {
  let capture: PendingCapture = { id: identity?.id ?? crypto.randomUUID(), userId, source, text, recording, transcript: undefined,
    createdAt: identity?.createdAt ?? new Date().toISOString(), attempts: 0, lastError: null, confirmed: false };
  try {
    await initialize();
    await mutationOwner(userId);
    if (rawRecordingId) capture = await transferRawRecordingToCapture(rawRecordingId, userId, text);
    const captures = rawRecordingId ? await loadCaptures() : await addCapture(capture);
    publish({ captures, error: null, loading: false });
    return capture;
  } catch (error) { problem(error); throw error; }
}
export async function discardPendingCapture(id: string): Promise<void> {
  try {
    if (!navigator.locks) throw new CaptureStorageError('unavailable');
    await navigator.locks.request(`cerebro-capture:${id}`, { ifAvailable: true }, async lock => {
      if (!lock) throw new CaptureStorageError('sending');
      const ownerId = await mutationOwner();
      const existing = (await loadCaptures()).find(c => c.id === id);
      if (existing?.confirmed) throw new CaptureStorageError('confirmed');
      const captures = await removeCapture(id, ownerId);
      publish({ captures, error: null, loading: false });
    });
  } catch (error) { problem(error); throw error; }
}
async function mutationOwner(expected?: string): Promise<string> {
  const { data: { session } } = await getSession();
  if (!session || (expected && session.user.id !== expected)) throw new CaptureStorageError('owner_changed');
  return session.user.id;
}
async function patch(id: string, ownerId: string, values: Parameters<typeof updateCapture>[2]) {
  await mutationOwner(ownerId);
  const captures = await updateCapture(id, ownerId, values);
  publish({ captures, error: null, loading: false });
}
export type SendOutcome = 'saved' | 'busy' | { error: string; serverSaved?: boolean };
/** 'saved' requires a confirmed server row and a completed local cleanup transaction. */
async function sendLockedCapture(id: string): Promise<SendOutcome> {
  let capture: PendingCapture | undefined;
  let stage: 'auth' | 'transcription' | 'insert' | 'cleanup' = 'auth';
  let serverSaved = false;
  try {
    await refresh();
    capture = current.captures.find(c => c.id === id);
    if (!capture) return 'busy';
    const { data: { session } } = await getSession();
    if (!session || session.user.id !== capture.userId) return { error: 'Sign in as the account that made this capture before sending it.' };
    if (capture.recording && !capture.transcript && !capture.confirmed) {
      stage = 'transcription';
      const transcript = await transcribeRecording(capture.recording.blob, capture.userId);
      await patch(id, capture.userId, { transcript });
      capture = { ...capture, transcript };
    }
    stage = 'insert';
    if (!capture.confirmed) {
      const words = capture.transcript ? combinedCapture(capture.text, capture.transcript) : capture.text;
      await insertCapture(words, capture.source, capture.id, capture.userId, capture.createdAt);
      serverSaved = true;
      await patch(id, capture.userId, { confirmed: true });
    } else serverSaved = true;
    stage = 'cleanup';
    await mutationOwner(capture.userId);
    const captures = await removeCapture(id, capture.userId);
    publish({ captures, error: null, loading: false });
    return 'saved';
  } catch (error) {
    const reason = error instanceof CaptureStorageError ? problem(error) :
      stage === 'auth' ? 'The signed-in session could not be restored; the capture remains on this device.' :
        stage === 'transcription' ? 'Transcription failed; the recording remains on this device for retry or download.' :
          stage === 'cleanup' ? 'Saved to Cerebro, but this browser could not remove its local copy.' : 'The server did not confirm this capture; it remains on this device.';
    if (capture && !(error instanceof CaptureStorageError)) {
      try { await patch(id, capture.userId, { attempts: capture.attempts + 1, lastError: reason }); }
      catch (storageError) { return { error: problem(storageError), serverSaved }; }
    }
    return { error: reason, serverSaved };
  }
}

/** The browser releases this exact-capture lock if its page closes or crashes. */
export async function sendPendingCapture(id: string): Promise<SendOutcome> {
  if (sending.has(id)) return 'busy';
  if (!navigator.locks) {
    const error = 'This browser cannot coordinate capture sending. Update the browser; this capture remains on this device.';
    publish({ ...current, error });
    return { error };
  }
  sending.add(id);
  try { return await navigator.locks.request(`cerebro-capture:${id}`, () => sendLockedCapture(id)); }
  catch {
    const error = 'The browser could not coordinate sending; this capture remains on this device.';
    publish({ ...current, error });
    return { error };
  }
  finally { sending.delete(id); }
}

/** Global sender interface retained; the queue/storage owns errors instead of silently dropping them. */
export function startPendingSender(onSaved: (id: string) => void): () => void {
  let timer: number | undefined;
  let stopped = false;
  const flush = async () => {
    window.clearTimeout(timer);
    if (stopped) return;
    try {
      await refresh();
      const { data: { session } } = await getSession();
      if (!session || stopped) return;
      for (const capture of [...current.captures].filter(c => c.userId === session.user.id)) {
        if (stopped) return;
        if ((await sendPendingCapture(capture.id)) === 'saved') onSaved(capture.id);
      }
    } catch (error) { problem(error); }
    if (stopped || current.captures.length === 0) return;
    const tries = Math.max(...current.captures.map(c => c.attempts));
    timer = window.setTimeout(() => void flush(), BACKOFF_MS[Math.min(Math.max(tries - 1, 0), BACKOFF_MS.length - 1)]);
  };
  const online = () => void flush();
  window.addEventListener('online', online);
  void flush();
  return () => { stopped = true; window.clearTimeout(timer); window.removeEventListener('online', online); };
}
